/** 기본 제공 점검표 법령명 */
export const LEG_BASIC_DEFAULT_LAW_NM = '산업안전보건법';

/** 점검표 추가 시 고정 옵션 (신규법령 추가) */
export const LEG_BASIC_LAW_SELECT_NEW_OPTION = {
  id: 'NEW',
  name: '신규법령 추가',
  description:
    '전기안전관리법, 위험물관리법, 소방시설법 등 사업장 자체 점검표를 만듭니다.',
};

/**
 * 서버 법령 목록 + 신규법령 추가로 선택 옵션 구성
 * @param {Array<{lawNm?: string, LawNm?: string, defaultYn?: string, DefaultYn?: string}>} lawRows
 */
export const BuildLegBasicLawSelectOptions = (lawRows = []) => {
  const seen = new Set();
  const options = [];

  const PushLaw = (lawNm, isDefault = false) => {
    const name = String(lawNm ?? '').trim();
    if (!name || seen.has(name)) return;
    seen.add(name);
    options.push({
      id: name,
      name,
      isDefault: isDefault || name === LEG_BASIC_DEFAULT_LAW_NM,
      canDelete: !(isDefault || name === LEG_BASIC_DEFAULT_LAW_NM),
      badge: name === LEG_BASIC_DEFAULT_LAW_NM ? '기본 제공' : null,
      description:
        name === LEG_BASIC_DEFAULT_LAW_NM
          ? '기본 제공되는 산업안전보건법 이행 점검표를 불러옵니다.'
          : '등록한 법령 점검표로 새 평가를 작성합니다. 이미 저장된 평가는 목록에서 조회·삭제만 가능합니다.',
    });
  };

  // 기본 법령은 항상 최상단
  PushLaw(LEG_BASIC_DEFAULT_LAW_NM, true);

  (lawRows || []).forEach((row) => {
    const name = row?.lawNm ?? row?.LawNm ?? '';
    const defaultYn = row?.defaultYn ?? row?.DefaultYn ?? 'N';
    PushLaw(name, defaultYn === 'Y');
  });

  options.push({ ...LEG_BASIC_LAW_SELECT_NEW_OPTION });
  return options;
};

export const LEG_BASIC_MODAL_APP_TITLE = '안전보건 관계법령 이행 점검';

/** 점검표 평가상태 (TB_BO_COMPLIANCE_EVAL.EVAL_STATUS) */
export const LEG_BASIC_EVA_STATUS = {
  DRAFT: 'DRAFT',
  SAVED: 'SAVED',
  CONFIRMED: 'CONFIRMED',
};

export const LEG_BASIC_EVA_STATUS_LABEL = {
  DRAFT: '작성중',
  SAVED: '임시저장',
  CONFIRMED: '저장완료',
};

/** API/그리드 행에서 평가상태 정규화 (레거시·미마이그레이션 → CONFIRMED) */
export const PickLegBasicEvalStatus = (row) => {
  const raw = row?.evalStatus ?? row?.EvalStatus ?? '';
  const key = String(raw).trim().toUpperCase();
  if (key === 'SAVED' || key === 'DRAFT') return key;
  return LEG_BASIC_EVA_STATUS.CONFIRMED;
};

export const FormatLegBasicEvalStatusLabel = (row) =>
  LEG_BASIC_EVA_STATUS_LABEL[PickLegBasicEvalStatus(row)] ||
  PickLegBasicEvalStatus(row);

/** 저장완료(CONFIRMED)만 조회 전용 */
export const IsLegBasicEvalReadOnly = (row) =>
  PickLegBasicEvalStatus(row) === LEG_BASIC_EVA_STATUS.CONFIRMED;

/** API datetime → YYYY-MM-DD (점검기간 표시·저장용) */
export const LegBasicToYmd = (raw) => {
  if (raw == null || raw === '') return '';
  const s = typeof raw === 'string' ? raw : String(raw);
  const t = s.indexOf('T');
  if (t > 0) return s.slice(0, 10);
  const sp = s.indexOf(' ');
  if (sp > 0) return s.slice(0, sp).slice(0, 10);
  return s.length >= 10 ? s.slice(0, 10) : s;
};

/** 이행여부 — API(Y/N/0) ↔ 화면(이행/미이행/해당없음) */
export const EncodeLegBasicCompliance = (value) => {
  const s = String(value ?? '').trim();
  if (!s) return '';
  if (s === 'Y' || s === '이행') return 'Y';
  if (s === 'N' || s === '미이행') return 'N';
  if (s === '0' || s === '해당없음' || s === '해당 없음') return '0';
  return s;
};

export const DecodeLegBasicComplianceForRadio = (value) => {
  const s = String(value ?? '').trim();
  if (s === 'Y') return '이행';
  if (s === 'N') return '미이행';
  if (s === '0') return '해당없음';
  return s;
};

/** API/그리드 필드명(camelCase·PascalCase·SCREAMING) 통합 조회 */
export const LegBasicPickField = (row, keys) => {
  for (let i = 0; i < keys.length; i++) {
    const v = row?.[keys[i]];
    if (v != null && v !== '') return v;
  }
  return '';
};

/** 모달 저장용 행 정규화 — evalId·텍스트·이행여부(Y/N/0) */
export const LegBasicNormalizeModalRowForSave = (row) => {
  if (!row || row.deleteYn === 'Y') return null;
  const compliance = EncodeLegBasicCompliance(
    LegBasicPickField(row, [
      'complianceStatus',
      'COMPLIANCE_STATUS',
      'lwCompliance',
      'LW_COMPLIANCE',
    ])
  );
  const evalId =
    LegBasicPickField(row, ['evalId', 'EvalId', 'lwCd', 'LwCd', 'EVAL_ID']) ??
    '';
  return {
    ...row,
    evalId: evalId !== '' ? String(evalId) : '',
    lwNm: LegBasicPickField(row, ['lwNm', 'LwNm', 'LW_NM']),
    checkItem: LegBasicPickField(row, [
      'checkItem',
      'CheckItem',
      'CHECK_ITEM',
      'lwProvisions',
      'LwProvisions',
    ]),
    checkContent: LegBasicPickField(row, [
      'checkContent',
      'CheckContent',
      'CHECK_CONTENT',
      'lwMain',
      'LwMain',
    ]),
    complianceStatus: compliance,
    remark: LegBasicPickField(row, ['remark', 'Remark', 'REMARK', 'lwRemark']),
    editYn: row.editYn === 'Y' || row.newYn === 'Y' || compliance !== '' ? 'Y' : '',
  };
};
